import * as React from 'react';
import type HotTable from '@handsontable/react';
import type { SpreadsheetV2Props } from '../SpreadsheetV2';
import { ERRORS_BATCH_UPDATE_EVENT_COMPLETED } from '../constants';

const useCustomSpreadsheetErrors = (
  hotTableRef: React.RefObject<HotTable>,
  errors: SpreadsheetV2Props['errors'] = [],
) => {
  const previousErrors = React.useRef<typeof errors>([]);

  React.useEffect(() => {
    const hotInstance = hotTableRef?.current?.hotInstance;

    if (!hotInstance || !(errors.length + previousErrors.current!.length)) {
      /*
        This is either the first render, or we don't have any errors to add or remove.
      */
      return;
    }
    hotInstance.batchRender(function render() {
      previousErrors.current?.forEach(error => {
        hotInstance.setCellMetaObject(error.row, error.col, {
          hasExternalError: false,
          valid: true,
        });

        const cell = hotInstance.getCell(error.row, error.col);

        if (cell) {
          cell.removeAttribute('data-error');
        }
      });

      errors.forEach(error => {
        /*
          For some unknown reason, setCellMetaObject only works correctly if we call
          getCellMeta first. If we don't, the cell will be marked as invalid, but the
          original values in the meta object will be removed, causing ErrorSlider to
          crash. See INK-1955.
        */
        hotInstance.getCellMeta(error.row, error.col);
        /*
          The line below sets `hasExternalError` on the cell meta properties as `true`.
          This is a custom property that we use to determine if the cell has an external error.
        */
        hotInstance.setCellMetaObject(error.row, error.col, {
          hasExternalError: true,
          externalErrorMessage: error.errorMessage,
          valid: false,
        });

        const cell = hotInstance.getCell(error.row, error.col);

        if (cell) {
          cell.dataset.error = error.errorMessage;
        }
      });
      hotInstance.render();
    });
    const event = new CustomEvent(ERRORS_BATCH_UPDATE_EVENT_COMPLETED);
    window.dispatchEvent(event);
    previousErrors.current = errors;
  }, [errors, hotTableRef]);
};

export { useCustomSpreadsheetErrors };
